import "@/App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";
import InstitutionalLayout from "./components/site/InstitutionalLayout";
import Landing from "./pages/site/Landing";
import SegmentPage from "./pages/site/SegmentPage";
import { Sobre, ComoFunciona, ParaEmpresas, Contato, LegalPage } from "./pages/site/InfoPages";
import { SEGMENTS } from "./lib/institutional";
import Feed from "./pages/Feed";
import Businesses from "./pages/Businesses";
import BusinessDetail from "./pages/BusinessDetail";
import NewsDetail from "./pages/NewsDetail";
import Profile from "./pages/Profile";
import Plans from "./pages/Plans";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CookieConsent from "./components/CookieConsent";
import AnalyticsRouteTracker from "./components/AnalyticsRouteTracker";
import NotFound from "./pages/NotFound";
import { captureRefFromURL } from "./lib/refCapture";

// Big / less-used pages are code-split to keep the initial bundle lean.
const AskAI = lazy(() => import("./pages/AskAI"));
const SearchPage = lazy(() => import("./pages/Search"));
const NewsCenter = lazy(() => import("./pages/NewsCenter"));
const AIOperatorPage = lazy(() => import("./pages/AIOperatorPage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const Insights = lazy(() => import("./pages/Insights"));
const Admin = lazy(() => import("./pages/Admin"));
const BusinessDashboard = lazy(() => import("./pages/BusinessDashboard"));
const MyBusiness = lazy(() => import("./pages/MyBusiness"));
const MyListings = lazy(() => import("./pages/MyListings"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ListingsList = lazy(() => import("./pages/vertical/ListingsList"));
const ListingDetail = lazy(() => import("./pages/vertical/ListingDetail"));
const VerticalProfile = lazy(() => import("./pages/vertical/VerticalProfile"));
const TrabalheConosco = lazy(() => import("./pages/TrabalheConosco"));
const GuiaComercialCadastro = lazy(() => import("./pages/GuiaComercialCadastro"));
const GuiaComercialDashboard = lazy(() => import("./pages/GuiaComercialDashboard"));
const JournalistDashboard = lazy(() => import("./pages/JournalistDashboard"));

const RouteFallback = () => (
  <div className="py-20 text-center text-muted-foreground text-sm">Carregando…</div>
);

function App() {
  useEffect(() => { captureRefFromURL(); }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* ===== Site institucional público ===== */}
            <Route element={<InstitutionalLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/como-funciona" element={<ComoFunciona />} />
              <Route path="/para-empresas" element={<ParaEmpresas />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/trabalhe-conosco" element={<TrabalheConosco />} />
              <Route path="/politica-de-privacidade" element={<LegalPage doc="politica-de-privacidade" />} />
              <Route path="/termos-de-uso" element={<LegalPage doc="termos-de-uso" />} />
              <Route path="/cookies" element={<LegalPage doc="cookies" />} />
              {SEGMENTS.map((s) => (
                <Route key={s.slug} path={`/${s.slug}`} element={<SegmentPage slug={s.slug} />} />
              ))}
            </Route>

            {/* ===== Aplicativo ===== */}
            <Route element={<Layout />}>
              <Route path="/app" element={<Navigate to="/feed" replace />} />
              <Route path="/feed" element={<Feed />} />
              <Route path="/ia" element={<AskAI />} />
              <Route path="/busca" element={<SearchPage />} />
              <Route path="/noticias" element={<NewsCenter />} />
              <Route path="/operador-ia" element={<AIOperatorPage />} />
              <Route path="/mapa" element={<MapPage />} />
              <Route path="/empresas" element={<Businesses />} />
              <Route path="/empresa/:slug" element={<BusinessDetail />} />
              <Route path="/noticia/:slug" element={<NewsDetail />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/planos" element={<Plans />} />
              <Route path="/painel" element={<BusinessDashboard />} />
              <Route path="/painel/anuncios" element={<MyListings />} />
              <Route path="/minha-empresa" element={<MyBusiness />} />
              <Route path="/guia-comercial" element={<GuiaComercialDashboard />} />
              <Route path="/guia-comercial/cadastro" element={<GuiaComercialCadastro />} />
              <Route path="/jornalista" element={<JournalistDashboard />} />

              <Route path="/imoveis" element={<ListingsList vertical="real_estate" />} />
              <Route path="/imoveis/:slug" element={<ListingDetail vertical="real_estate" />} />
              <Route path="/corretor/:slug" element={<VerticalProfile vertical="real_estate" />} />

              <Route path="/medicos" element={<ListingsList vertical="health" />} />
              <Route path="/medicos-publicacao/:slug" element={<ListingDetail vertical="health" />} />
              <Route path="/medico/:slug" element={<VerticalProfile vertical="health" />} />

              <Route path="/veiculos" element={<ListingsList vertical="automotive" />} />
              <Route path="/veiculos/:slug" element={<ListingDetail vertical="automotive" />} />
              <Route path="/loja-veiculos/:slug" element={<VerticalProfile vertical="automotive" />} />

              <Route path="/pagamento/sucesso" element={<PaymentResult />} />
              <Route path="/pagamento/falha" element={<PaymentResult />} />
              <Route path="/pagamento/pendente" element={<PaymentResult />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Register />} />
              <Route path="/esqueci-senha" element={<ForgotPassword />} />
              <Route path="/redefinir-senha" element={<ResetPassword />} />
              <Route path="/admin" element={<Admin />} />

              {/* 404 — must be inside Layout so header/nav stay */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </Suspense>
          <CookieConsent />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
